import {
  AddMembersClubResponse,
  ApproveMembersClubResponse,
  GetClubDetailResponse,
  GetClubEventsResponse,
  GetClubInvitesResponse,
  GetClubMatchDetailResponse,
  GetClubMatchesResponse,
  GetClubMembersResponse,
  GetClubPaymentLinkResponse,
  GetClubRankingsResponse,
  GetClubRolesResponse,
  GetClubsResponse,
  GetMyClubResponse,
  GetStaffClubResponse,
  InviteClubMemberResponse,
  JoinClubResponse,
  LeaveClubResponse,
  ParamsGetClubEvents,
  ParamsRemoveMember,
  ParamsUninviteMembers,
  ParamsDeclineJoinRequests,
  PayloadAddMembers,
  PayloadApproveMember,
  PayloadClubInviteMember,
  PayloadDeleteClubMatch,
  PayloadGetClubInvites,
  PayloadGetClubMatches,
  PayloadGetClubMembers,
  PayloadGetClubPaymentLink,
  PayloadGetClubRankings,
  PayloadGetClubRoles,
  PayloadGetClubs,
  PayloadRemoveAssignOrganizer,
  PayloadSearchClubMembers,
  PayloadUpdateClub,
  RemoveMemberClubResponse,
  Response,
  ClubMembersManagementPayload,
  ClubMemberEntitlementsRequest,
  ClubMemberEntitlementsResponse,
  ClubEntitlementOptionsResponse,
  GetClubSettingsResponse,
  UpdateClubSettingsRequest,
  ClubMembersByStateRequest,
  ClubMembersByStateResponse,
} from '@/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import httpService from './httpService'

const clubPath = '/club/v1.0'
const clubRolePath = '/club/roles/v1.0'
const clubMatchPath = '/club/match/v1.0/history'
const eventClubPath = '/event/club/v1.0'

export enum ClubQueryKeys {
  GET_CLUB_RANKINGS = 'get_club_rankings',
  GET_CLUB_MEMBERS = 'get_club_members',
  GET_CLUB_RESTRICTIONS = 'get_club_restrictions',
  SEARCH_CLUB_MEMBERS = 'search_club_members',
  GET_CLUB_INVITES = 'get_club_invites',
  GET_MY_CLUBS = 'get_my_clubs',
  GET_CLUBS = 'get_clubs',
  GET_CLUB_MANAGEMENT_ROLES = 'get_club_management_roles',
  GET_CLUB_MEMBERS_MANAGEMENT = 'get_club_members_management',
  GET_CLUB_MEMBERS_BY_STATE = 'get_club_members_by_state',
  GET_CLUB_MEMBER_ENTITLEMENTS = 'get_club_member_entitlements',
  GET_ENTITLEMENT_OPTIONS = 'get_entitlement_options',
  GET_CLUB_SETTINGS = 'get_club_settings',
}

export const getMyClubs = async () => {
  return await httpService.Get<GetMyClubResponse>(`${clubRolePath}/all`)
}

export const getClubs = async (payload: PayloadGetClubs) => {
  return await httpService.Post<GetClubsResponse>(`${clubPath}/all`, payload)
}

export const getClubDetail = async (clubId: string) => {
  return await httpService.Get<GetClubDetailResponse>(`${clubPath}/${clubId}`)
}

export const clubRoles = async (payload: PayloadGetClubRoles) => {
  return await httpService.Post<GetClubRolesResponse>(`/club/${payload.clubId}/roles/v1.0/permission`, {
    userId: payload.userId,
  })
}

export const getClubMembers = async (clubId: number, payload: PayloadGetClubMembers) => {
  return await httpService.Post<GetClubMembersResponse>(`/club/${clubId}/members/v1.0/all`, payload)
}

export const useGetClubRankings = (clubId: number, limit: number, offset: number, query: string, sortParam: string) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_RANKINGS, clubId, limit, offset, query, sortParam],
    async ({ signal }) => {
      const payload: Partial<PayloadGetClubRankings> = {
        limit: limit,
        offset: offset,
        query: query,
        sort: {
          order: 'DESC',
          parameter: sortParam,
        },
      }
      return await httpService.Post<GetClubRankingsResponse>(`/club/${clubId}/v1.0/ranking`, payload, { signal })
    },
    {
      enabled: !!clubId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )
}

export const useGetClubMembers = (
  clubId: number,
  limit: number,
  filterLat: number | null,
  filterLng: number | null,
  offset: number,
  query: string
) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_MEMBERS, clubId, limit, filterLat, filterLng, offset, query],
    async ({ signal }) => {
      const payload: Partial<PayloadGetClubMembers> = {
        exclude: [],
        limit: limit,
        filter: {
          lat: filterLat,
          lng: filterLng,
        },
        offset: offset,
        query: query,
      }
      const res = await httpService.Post(`/club/${clubId}/members/v1.0/all`, payload, { signal })
      return res as GetClubMembersResponse
    },
    {
      enabled: !!clubId,
      cacheTime: 0,
    }
  )
}

export const useGetClubMatches = () => {
  return useMutation([], async (payload: PayloadGetClubMatches) => {
    const res = await httpService.Post(clubMatchPath, payload)
    return res as GetClubMatchesResponse
  })
}

export const useGetClubEvents = () => {
  return useMutation([], async (params: ParamsGetClubEvents) => {
    const res = await httpService.Get(
      `${eventClubPath}/${params.clubId}?limit=${params.limit}&offset=${params.offset}&includeDraftEvents=${params.includeDraftEvents}`
    )
    return res as GetClubEventsResponse
  })
}

export const useGetClubInvites = (payload: PayloadGetClubInvites) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_INVITES, JSON.stringify(payload)],
    async ({ signal }) => {
      const { clubId, ...restPayload } = payload
      const res = await httpService.Post(`club/${clubId}/members/v1.0/pending/invites`, restPayload, { signal })
      return res as GetClubInvitesResponse
    },
    {
      enabled: !!payload.clubId,
      cacheTime: 0,
    }
  )
}

export const useAddMembers = () => {
  return useMutation([], async (payload: PayloadAddMembers) => {
    const { clubId, ...restPayload } = payload

    const res = await httpService.Put(`club/${clubId}/members/v1.0/add`, restPayload)
    return res as AddMembersClubResponse
  })
}

export const useRemoveMember = () => {
  return useMutation([], async (params: ParamsRemoveMember) => {
    const qs = params.userIds.map((id) => `userIds=${id}`).join('&')
    const res = await httpService.Delete(`club/${params.clubId}/members/v1.0/remove?${qs}`)
    return res as RemoveMemberClubResponse
  })
}

export const useUninviteMembers = () => {
  return useMutation([], async (params: ParamsUninviteMembers) => {
    const res = await httpService.Post(`club/${params.clubId}/members/v1.0/uninvite`, { userIds: params.userIds })
    return res as Response
  })
}

export const useDeclineJoinRequests = () => {
  return useMutation([], async (params: ParamsDeclineJoinRequests) => {
    const res = await httpService.Post(`club/${params.clubId}/members/v1.0/decline-requests`, {
      userIds: params.userIds,
    })
    return res as Response
  })
}

export const useApproveMembers = () => {
  return useMutation([], async (payload: PayloadApproveMember) => {
    const { clubId, ...restPayload } = payload
    const res = await httpService.Post(`club/${clubId}/v1.0/approve`, restPayload)
    return res as ApproveMembersClubResponse
  })
}

export const useLeaveClub = () => {
  return useMutation([], async (clubId: number) => {
    const res = await httpService.Delete(`club/${clubId}/members/v1.0/leave`)
    return res as LeaveClubResponse
  })
}

export const useJoinClub = () => {
  return useMutation([], async (clubId: number) => {
    const res = await httpService.Put(`club/${clubId}/members/v1.0/join`, {})
    return res as JoinClubResponse
  })
}

export const useGetClubStaff = () => {
  return useMutation([], async (clubId: number) => {
    const res = await httpService.Get(`club/${clubId}/members/v1.0/staff`)
    return res as GetStaffClubResponse
  })
}

export const useEditClubStaff = () => {
  return useMutation([], async (payload: any) => {
    const { clubId } = payload
    const res = await httpService.Put(`club/${clubId}/members/v1.0/staff`, payload)
    return res as GetStaffClubResponse
  })
}

export const useUpdateClub = () => {
  return useMutation([], async (payload: PayloadUpdateClub) => {
    const res = await httpService.Post('club/v1.0/update', payload)
    return res as Response
  })
}

export const useRemoveOrganizer = () => {
  return useMutation([], async (payload: PayloadRemoveAssignOrganizer) => {
    const { clubId, ...restPayload } = payload
    const res = await httpService.Post(`club/${clubId}/roles/v1.0/remove`, restPayload)
    return res as Response
  })
}

export const useAssignOrganizer = () => {
  return useMutation([], async (payload: PayloadRemoveAssignOrganizer) => {
    const { clubId, ...restPayload } = payload
    const res = await httpService.Post(`club/${clubId}/roles/v1.0/assign`, restPayload)
    return res as Response
  })
}

export const useGetClubPaymentLink = () => {
  return useMutation([], async (payload: PayloadGetClubPaymentLink) => {
    if (payload.accountId && !payload.pendingRequirement && payload.detailsSubmitted) {
      return await httpService.Get<GetClubPaymentLinkResponse>(`/payment/club/${payload.clubId}/v1.0/dashboard`)
    }
    return await httpService.Get<GetClubPaymentLinkResponse>(`/payment/club/${payload.clubId}/v1.0/setup`)
  })
}

export const useSearchClubMembers = (payload: PayloadSearchClubMembers) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_INVITES, JSON.stringify(payload)],
    async ({ signal }) => {
      const { clubId, ...restPayload } = payload
      const res = await httpService.Post(`club/${clubId}/members/v1.0/all`, restPayload, { signal })
      return res as GetClubMembersResponse
    },
    {
      enabled: !!payload.clubId,
      cacheTime: 0,
    }
  )
}

export const useDeleteClubMatch = () => {
  return useMutation([], async (payload: PayloadDeleteClubMatch) => {
    const { clubId, ...restPayload } = payload
    const res = await httpService.Post(`club/${clubId}/match/${restPayload.matchId}/v1.0/delete`, restPayload)
    return res as any
  })
}

export const useGetClubMatchDetail = (clubId?: string, matchId?: string) => {
  return useQuery(
    [clubId, matchId],
    async () => {
      return await httpService.Get<GetClubMatchDetailResponse>(`club/${clubId}/match/${matchId}/v1.0/get`)
    },
    {
      enabled: !!clubId && !!matchId,
    }
  )
}

export const useInviteClubMember = () => {
  return useMutation([], async (payload: PayloadClubInviteMember) => {
    const { clubId, ...restPayload } = payload
    const res = await httpService.Put(`club/${clubId}/members/v1.0/invite`, restPayload)
    return res as InviteClubMemberResponse
  })
}

/** Where a background member export has got to. `RUNNING` covers "queued". `s3Url` arrives with `COMPLETED`. */
export interface ClubMembersExportRun {
  runId: string
  status: 'RUNNING' | 'COMPLETED' | 'FAILED'
  s3Url?: string
}

/**
 * Starts the member CSV export and resolves to the run to poll.
 *
 * The export is built in the background because a large club's file takes longer than a
 * load-balancer will hold a connection open: the old single request finished on the server but
 * never arrived. A caller over the hourly or daily limit is refused here, with a 400 whose
 * `message` says so.
 */
export const startClubMembersExport = async (clubId: string | number): Promise<string> => {
  const res = await httpService.Post<{ result?: ClubMembersExportRun }>(`club/${clubId}/members/v1.0/all/export`, {})
  const runId = res.result?.runId
  if (!runId) throw new Error('The export started but did not say which run to follow')
  return runId
}

export const pollClubMembersExport = async (clubId: string | number, runId: string): Promise<ClubMembersExportRun> => {
  const res = await httpService.Get<{ result: ClubMembersExportRun }>(`club/${clubId}/members/v1.0/all/export/${runId}`)
  return res.result
}

export const useGetMyClubs = (enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_MY_CLUBS],
    async () => {
      return await httpService.Get<GetMyClubResponse>(`${clubRolePath}/all`)
    },
    {
      cacheTime: 15 * 60 * 1000, // 15 minutes - user's clubs don't change frequently
      staleTime: 5 * 60 * 1000, // 5 minutes fresh time
      refetchOnWindowFocus: false,
      enabled: enabled,
    }
  )
}

export const useGetClubs = (payload: PayloadGetClubs, enabled = true) => {
  return useQuery({
    queryKey: [ClubQueryKeys.GET_CLUBS, JSON.stringify(payload)],
    queryFn: ({ signal }) => {
      return httpService.Post<GetClubsResponse>(`${clubPath}/all`, payload, { signal })
    },
    refetchOnWindowFocus: false,
    cacheTime: 10 * 60 * 1000, // 10 minutes - club search results can be cached briefly
    staleTime: 2 * 60 * 1000, // 2 minutes fresh time for search results
    enabled: enabled,
  })
}

// Club Management API Functions
export const getClubManagementRoles = async () => {
  return await httpService.Get<GetMyClubResponse>(`${clubRolePath}/all`)
}

export const uploadBulkMatchesCSV = async (file: File) => {
  const formData = new FormData()
  formData.append('request', file)
  return await httpService.Put('/club/match/csv/add/bulk', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

// React Query hooks for club management
export const useGetClubManagementRoles = (enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_MANAGEMENT_ROLES],
    async () => {
      return await httpService.Get<GetMyClubResponse>(`${clubRolePath}/all`)
    },
    {
      cacheTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
      enabled: enabled,
    }
  )
}

// Optimized query hook for club details with caching
export const useGetClubDetailQuery = (clubId: string, enabled = true) => {
  return useQuery(
    ['club-detail', clubId],
    async () => {
      return await httpService.Get<GetClubDetailResponse>(`${clubPath}/${clubId}`)
    },
    {
      cacheTime: 20 * 60 * 1000, // 20 minutes - club details are relatively stable
      staleTime: 5 * 60 * 1000, // 5 minutes fresh time
      refetchOnWindowFocus: false,
      enabled: enabled && !!clubId,
    }
  )
}

export const useUploadBulkMatchesCSV = () => {
  return useMutation([], async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await httpService.Post('/club/match/csv/add/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return res
  })
}

// Club Members Management (with full filter/sort/pagination)
export const useGetClubMembersManagement = (clubId: number, payload: ClubMembersManagementPayload, enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_MEMBERS_MANAGEMENT, clubId, JSON.stringify(payload)],
    async ({ signal }) => {
      return await httpService.Post<GetClubMembersResponse>(`/club/${clubId}/members/v1.0/all`, payload, { signal })
    },
    {
      enabled: enabled && !!clubId,
      keepPreviousData: true,
      cacheTime: 2 * 60 * 1000,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )
}

// Club Members by State (simpler, queries club_members table directly)
export const useGetClubMembersByState = (clubId: number, payload: ClubMembersByStateRequest, enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_MEMBERS_BY_STATE, clubId, JSON.stringify(payload)],
    async ({ signal }) => {
      return await httpService.Post<ClubMembersByStateResponse>(`/club/${clubId}/members/v1.0/by-state`, payload, {
        signal,
      })
    },
    {
      enabled: enabled && !!clubId,
      keepPreviousData: true,
      cacheTime: 2 * 60 * 1000,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )
}

// Club Pending Invites (ES-backed, with full search/filter/sort)
export const useGetClubPendingInvites = (clubId: number, payload: ClubMembersManagementPayload, enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_INVITES, clubId, JSON.stringify(payload)],
    async ({ signal }) => {
      return await httpService.Post<GetClubMembersResponse>(`/club/${clubId}/members/v1.0/pending/invites`, payload, {
        signal,
      })
    },
    {
      enabled: enabled && !!clubId,
      keepPreviousData: true,
      cacheTime: 2 * 60 * 1000,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  )
}

// Club Member Entitlements
export const useGetClubMemberEntitlements = (
  clubId: number,
  payload: ClubMemberEntitlementsRequest,
  enabled = true
) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_MEMBER_ENTITLEMENTS, clubId, JSON.stringify(payload)],
    async ({ signal }) => {
      return await httpService.Post<ClubMemberEntitlementsResponse>(
        `/club/${clubId}/members/v1.0/entitlements`,
        payload,
        { signal }
      )
    },
    {
      enabled: enabled && !!clubId && payload.userIds.length > 0,
      cacheTime: 2 * 60 * 1000,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    }
  )
}

// Entitlement Options for filter dropdowns
export const useGetEntitlementOptions = (clubId: number, enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_ENTITLEMENT_OPTIONS, clubId],
    async ({ signal }) => {
      return await httpService.Get<ClubEntitlementOptionsResponse>(
        `/club/${clubId}/members/v1.0/entitlements/options`,
        { signal }
      )
    },
    {
      enabled: enabled && !!clubId,
      cacheTime: 10 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )
}

// Club Settings
export const useGetClubSettings = (clubId: number, enabled = true) => {
  return useQuery(
    [ClubQueryKeys.GET_CLUB_SETTINGS, clubId],
    async ({ signal }) => {
      return await httpService.Get<GetClubSettingsResponse>(`/club/${clubId}/v1.0/settings`, { signal })
    },
    {
      enabled: enabled && !!clubId,
      cacheTime: 5 * 60 * 1000,
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )
}

// Update Club Settings
export const useUpdateClubSettings = () => {
  return useMutation([], async ({ clubId, payload }: { clubId: number; payload: UpdateClubSettingsRequest }) => {
    return await httpService.Put(`/club/${clubId}/v1.0/settings`, payload)
  })
}
